import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("admintoken");
    if (token) {
      config.headers.admintoken = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          console.log("401 error detected");
          throw new Error("Session expired. Please log in again.");

        case 403:
          throw new Error("You do not have permission to perform this action.");

        case 404:
          throw new Error("The requested resource was not found.");

        case 422:
          throw new Error(data.message || "Validation failed.");

        case 500:
          throw new Error("Server error. Please try again later.");

        default:
          throw new Error(
            data.message || `Request failed with status ${status}`
          );
      }
    } else if (error.request) {
      throw new Error(
        "Network error. Please check your connection and try again."
      );
    } else {
      throw new Error("An unexpected error occurred. Please try again.");
    }
  }
);

/**
 * Admin API
 *
 * NOTE: Location management endpoints (countries, provinces, cities, suburbs)
 * have been removed. Locations are now coordinate-based and reverse-geocoded
 * via Nominatim — there's no admin-curated location tree to manage.
 */
export const adminApi = {
  // Admin login
  loginAdmin: async (username, password) => {
    return api.post("/user/admin", { username, password });
  },

  /**
   * Categories
   */
  getAllCategories: async () => {
    return api.get("/category");
  },

  createCategory: async (data) => {
    return api.post("/category", data);
  },

  updateCategory: async (id, data) => {
    return api.put(`/category/${id}`, data);
  },

  deleteCategory: async (id) => {
    return api.delete(`/category/${id}`);
  },

  /**
   * Users
   */
  getAllUsers: async () => {
    return api.get("/admin/users");
  },

  /**
   * Listings
   */
  getListingsByUser: async (id) => {
    return api.get(`/admin/listing/${id}`);
  },

  /**
   * Reports
   */
  getAllReports: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/reports${queryString ? `?${queryString}` : ""}`);
  },

  getReportById: async (id) => {
    return api.get(`/reports/${id}`);
  },

  getRelatedReports: async (type, targetId) => {
    return api.get(`/reports/related/${type}/${targetId}`);
  },

  getUserSubmittedReports: async (userId) => {
    return api.get(`/reports?reportedBy=${userId}`);
  },

  updateReport: async (id, data) => {
    return api.put(`/reports/${id}`, data);
  },

  bulkResolveReports: async (data) => {
    return api.post("/reports/bulk-resolve", data);
  },

  getReportStats: async () => {
    return api.get("/reports/stats");
  },

  warnUser: async (userId, reason) => {
    return api.post(`/reports/actions/warn`, { userId, reason });
  },

  banUser: async (userId, reason, duration = null) => {
    const action = duration ? "temporary_ban" : "permanent_ban";
    return api.post(`/reports/actions/ban`, {
      userId,
      reason,
      action,
      banDuration: duration,
    });
  },

  removeUserListings: async (userId, reason) => {
    return api.post(`/reports/actions/remove-listings`, { userId, reason });
  },

  dismissReport: async (reportId, data) => {
    return api.post(`/reports/${reportId}/dismiss`, data);
  },

  /**
   * Messages / Conversations
   */
  getUserConversations: async (id) => {
    return api.get(`/messages/admin/${id}`);
  },

  getConversations: async () => {
    return api.get("/messages/admin/conversations");
  },

  getConversationDetails: async (id) => {
    return api.get(`/messages/admin/conversations/${id}`);
  },

  getFlaggedConversations: async () => {
    return api.get("/message/admin/flagged");
  },

  getConversationMessages: async (conversationId) => {
    return api.get(`/message/conversation/${conversationId}`);
  },

  deleteMessage: async (messageId) => {
    return api.delete(`/message/${messageId}`);
  },

  flagMessage: async (messageId, reason) => {
    return api.put(`/message/flag/${messageId}`, { reason });
  },

  /**
   * Dashboard
   */
  getDashboardStats: async () => {
    return api.get("/admin");
  },

  /**
   * Revert Actions
   */
  unbanUser: async (userId) => {
    return api.put(`/admin/unban-user/${userId}`);
  },

  reactivateListing: async (listingId) => {
    return api.put(`/admin/reactivate-listing/${listingId}`);
  },

  restoreConversation: async (conversationId) => {
    return api.put(`/admin/restore-conversation/${conversationId}`);
  },

  /**
   * Location Requests (separate system, kept for now)
   */
  getAllLocationRequests: async () => {
    return api.get("/location-requests");
  },

  /**
 * Resources
 */
  getAllResources: async () => {
    return api.get("/resources/admin/all");
  },

  getResource: async (id) => {
    return api.get(`/resources/${id}`);
  },

  createResource: async (data) => {
    return api.post("/resources", data);
  },

  fetchResourcePreview: async (url) => {
    return api.post("/resources/fetch-preview", { url });
  },

  uploadResourceImage: async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    return api.post("/resources/upload-image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  updateResource: async (id, data) => {
    return api.put(`/resources/${id}`, data);
  },

  publishResource: async (id) => {
    return api.put(`/resources/${id}/publish`);
  },

  unpublishResource: async (id) => {
    return api.put(`/resources/${id}/unpublish`);
  },

  deleteResource: async (id) => {
    return api.delete(`/resources/${id}`);
  },
};